/* Хвост заказа товарных экранов (APP-SPEC-5 §1.4, §11 «Хвост», §13.2, §14 «Хвост», §16) поверх общего OrderTail (BotRun):
   герой с дугами этапов сервера, заголовок, чек, «Открыть в чате», отказ и сбой — у OrderTail; здесь — своё у экрана:
   у «Карточки» — вкладки по секциям результата («Фото · Слайды · Видео · Описание») с подписью и «Изменить» в свой инструмент;
   у «Слайдов» и «Карусели» — карусель слайдов с правкой текста на месте (PATCH /app/orders/{id}/slides/{n}) и «Текст поста».
   Номера заказа на итоге нет, «Отправить в чат» не ставится: бот присылает сам. Всё — из ответа сервера. */
import { useMemo, useState, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../i18n';
import type { CircleFormState } from '../studio/circle/CircleFieldInput';
import { OrderTail, type TailAction, type TailTexts } from '../studio/circle/OrderTail';
import { leaveTail } from '../studio/circle/tool-helpers';
import { openStudioTarget } from '../studio/studio-navigation';
import {
  andList,
  count,
  DESC_TONES,
  INFOG_STYLES,
  label,
  MCARD_MOTIONS,
  MCARD_SETS,
  PHOTO_FORMS,
  PLATFORMS,
  POST_FORMATS,
  POST_STYLES,
  POST_TONES,
  SLIDE_FORMS,
  stopWords,
  type ProductScreen,
} from './product-model';
import { downloadArchive, saveBlob, useProductAssets, type LoadedAsset, type ResultSlide } from './use-product-result';
import { useSlideEdit } from './use-slide-edit';

const TAB: Record<string, string> = { photos: 'Фото', slides: 'Слайды', video: 'Видео', description: 'Описание' };
const DONE_WORD: Record<string, string> = { photos: 'фото', slides: 'слайды', video: 'видео', description: 'описание' };
const TAB_ORDER = ['photos', 'slides', 'video', 'description'];
const TEXTS: Record<ProductScreen, Omit<TailTexts, 'doneP'> & { doneP: string; reject: string }> = {
  mcard: {
    runH: 'Собираем карточку',
    runP: 'Можно уйти с экрана: бот пришлёт всё в чат, когда будет готово.',
    doneH: 'Карточка готова',
    doneP: 'Бот уже прислал всё в чат, в отдельную тему.',
    modH: 'Карточка не прошла проверку',
    techH: 'Не получилось собрать карточку',
    notMade: 'карточка не создана',
    reject: 'Изменить фото или текст',
  },
  infog: {
    runH: 'Собираем слайды',
    runP: 'Можно уйти с экрана: бот напишет, когда слайды будут готовы.',
    doneH: 'Слайды готовы',
    doneP: 'Бот уже прислал их в чат, в отдельную тему. Текст набран шаблоном, без ошибок нейросети: нажмите «Изменить текст», чтобы исправить.',
    modH: 'Слайды не прошли проверку',
    techH: 'Не получилось собрать слайды',
    notMade: 'слайды не созданы',
    reject: 'Изменить текст',
  },
  post: {
    runH: 'Собираем карусель',
    runP: 'Можно уйти с экрана: бот напишет, когда карусель будет готова.',
    doneH: 'Карусель готова',
    doneP: 'Бот уже прислал карусель с текстом поста в чат, в отдельную тему. Текст правится прямо на слайде, без новой генерации.',
    modH: 'Карусель не прошла проверку',
    techH: 'Не получилось собрать карусель',
    notMade: 'карусель не создана',
    reject: 'Изменить тему',
  },
};
/** Ключи формы, под которыми ушли входы заказа (у каждого экрана свой набор). */
export interface ResultKeys {
  photos: string[];
  text?: string;
  outputs?: string;
  photoCount?: string;
  platform?: string;
  photoSet?: string;
  slidesStyle?: string;
  videoMotion?: string;
  descTone?: string;
  style?: string;
  format?: string;
  tone?: string;
}
type Inputs = Record<string, string | undefined>;
const groupDigits = (value: number) => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

async function copy(value: string) {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}
function TextBlock({ title, value, limit }: { title: string; value: string; limit?: number }) {
  const [copied, setCopied] = useState(false);
  return (
    <section className="px-text">
      <div className="px-text-h">
        <b>{uiText(title)}</b>
        {limit !== undefined && <span className="mono">{uiFormat('{0} из {1} знаков', value.length, groupDigits(limit))}</span>}
      </div>
      <pre>{value}</pre>
      <button type="button" className="btn sec" onClick={() => void copy(value).then(setCopied)}>
        {uiText(copied ? 'Скопировано' : 'Скопировать')}
      </button>
    </section>
  );
}

/** Карусель слайдов во всю колонку, лента миниатюр и правка текста показанного слайда. */
function SlideCarousel({
  kind,
  items,
  slides,
  edit,
  locked,
}: {
  kind: ProductScreen;
  items: LoadedAsset[];
  slides: ResultSlide[];
  edit?: { save(slide: number, title: string, body: string): Promise<boolean>; busy: boolean; pending: boolean };
  locked: boolean;
}) {
  const [at, setAt] = useState(0);
  const [draft, setDraft] = useState<{ title: string; body: string } | null>(null);
  const position = Math.min(at, items.length - 1);
  const current = items[position];
  if (!current) return null;
  const slide = slides.find(item => item.id === current.index);
  const name = kind === 'infog' && position === 0 ? uiText('Обложка') : uiFormat('Слайд {0}', position + 1);
  const go = (next: number) => {
    setDraft(null);
    setAt(Math.max(0, Math.min(items.length - 1, next)));
  };
  return (
    <div className="px-slides">
      <div className="px-stage">
        <button type="button" className="px-nav" aria-label={uiText('Предыдущий слайд')} disabled={position === 0} onClick={() => go(position - 1)}>
          ‹
        </button>
        <figure>
          <img src={current.url} alt={`${name} · ${position + 1} / ${items.length}`} />
          <figcaption>
            {name} · <span className="mono">{position + 1} / {items.length}</span>
          </figcaption>
        </figure>
        <button
          type="button"
          className="px-nav"
          aria-label={uiText('Следующий слайд')}
          disabled={position === items.length - 1}
          onClick={() => go(position + 1)}
        >
          ›
        </button>
      </div>
      {items.length > 1 && (
        <div className="px-thumbs" role="tablist" aria-label={uiText('Слайды')}>
          {items.map((item, index) => (
            <button
              key={item.index}
              type="button"
              role="tab"
              aria-selected={index === position}
              aria-label={uiFormat('Слайд {0}', index + 1)}
              onClick={() => go(index)}
            >
              <img src={item.url} alt="" />
            </button>
          ))}
        </div>
      )}
      {edit && slide && !draft && (
        <button
          type="button"
          className="btn sec px-edit"
          disabled={locked || edit.busy || edit.pending}
          onClick={() => setDraft({ title: slide.title || (slide.body ? '' : slide.text), body: slide.body })}
        >
          {uiText('Изменить текст')}
        </button>
      )}
      {edit && slide && draft && (
        <form
          className="px-editor"
          onSubmit={event => {
            event.preventDefault();
            void edit.save(slide.id, draft.title.trim(), draft.body.trim()).then(saved => {
              if (saved) setDraft(null);
            });
          }}
        >
          <label>
            {uiText('Заголовок')} <span className="mono">{draft.title.length} / 90</span>
            <input value={draft.title} maxLength={90} disabled={edit.busy} onChange={event => setDraft({ ...draft, title: event.target.value })} />
          </label>
          <label>
            {uiText('Текст')} <span className="mono">{draft.body.length} / 240</span>
            <textarea value={draft.body} maxLength={240} disabled={edit.busy} onChange={event => setDraft({ ...draft, body: event.target.value })} />
          </label>
          <p className="hint">{uiText('Перерисуем слайд тем же шаблоном, без новой генерации.')}</p>
          <div className="px-row">
            <button type="submit" className="btn" disabled={edit.busy || (!draft.title.trim() && !draft.body.trim())}>
              {uiText(edit.busy ? 'Сохраняем…' : 'Сохранить')}
            </button>
            <button type="button" className="btn sec" disabled={edit.busy} onClick={() => setDraft(null)}>
              {uiText('Отмена')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export function ProductOrderResult({
  state,
  kind,
  keys,
  hero,
  onRetry,
}: {
  state: CircleFormState;
  kind: ProductScreen;
  keys: ResultKeys;
  /** Главное фото товара для героя статуса */
  hero?: string;
  /** «Попробовать ещё раз» (useTailRetry формы) */
  onRetry?(): void;
}) {
  const { orderId, order, busy, submissionBlocked } = state;
  const edit = useSlideEdit(state.ownerId, kind === 'mcard' ? null : order);
  const shown = kind === 'mcard' ? order : (edit.order ?? order);
  const loaded = useProductAssets(shown?.status === 'done' ? shown : null);
  // Подписи итога — по входам, которые действительно ушли в заказ (снимок намерения), а не по черновику формы.
  const inputs = (state.intent?.body?.inputs ?? state.inputs) as Inputs;
  const sections = useMemo(() => {
    const byKind = new Map(loaded.shape.sections.map(section => [section.kind, section]));
    return TAB_ORDER.filter(item => byKind.has(item)).map(item => byKind.get(item)!);
  }, [loaded.shape.sections]);
  const [tab, setTab] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  if (!orderId) return null;
  const texts = TEXTS[kind];
  const done = shown?.status === 'done';
  const off = busy || submissionBlocked;
  const value = (key: string | undefined) => (key ? inputs[key] : undefined);
  const platform = value(keys.platform);
  const platformName = label(PLATFORMS, platform);
  const platformSize = platform ? PLATFORMS[platform]?.size : undefined;
  const assetsOf = (indices: number[]) => loaded.items.filter(item => indices.includes(item.index));
  const images = loaded.items.filter(item => item.mime.startsWith('image/'));
  const activeTab = sections.find(section => section.kind === tab) ?? sections[0];
  const leave = () => void leaveTail(state);
  const download = async (url: string, name: string, fallback?: LoadedAsset) => {
    setMessage('');
    try {
      if (url && shown) saveBlob(await downloadArchive(shown, url), name);
      else if (fallback) saveBlob(fallback.blob, fallback.name || name);
    } catch {
      setMessage('Не удалось скачать файл. Попробуйте снова.');
    }
  };
  const caption = (section: string, total: number) => {
    const parts: string[] = [];
    if (section === 'photos') {
      parts.push(`${count(total, PHOTO_FORMS)} ${uiText('в сценах')}`);
      if (value(keys.photoSet)) parts.push(uiFormat('Набор «{0}»', label(MCARD_SETS, value(keys.photoSet))));
    }
    if (section === 'slides') {
      parts.push(`${count(total, SLIDE_FORMS)} ${uiText('с преимуществами')}`);
      if (value(keys.slidesStyle)) parts.push(uiFormat('Оформление «{0}»', label(INFOG_STYLES, value(keys.slidesStyle))));
    }
    if (section === 'video') {
      parts.push(uiText('Видео товара'));
      if (value(keys.videoMotion)) parts.push(label(MCARD_MOTIONS, value(keys.videoMotion)));
      const video = assetsOf(activeTab?.indices ?? []).find(item => item.mime.startsWith('video/'));
      if (video?.hasAudio !== undefined) parts.push(uiText(video.hasAudio ? 'со звуком' : 'без звука'));
    }
    if (section === 'description') {
      parts.push(uiText('Описание товара'));
      if (value(keys.descTone)) parts.push(uiFormat('стиль «{0}»', label(DESC_TONES, value(keys.descTone))));
    }
    if (section !== 'video' && platformName) parts.push(platformName);
    if ((section === 'photos' || section === 'slides') && platformSize) parts.push(platformSize);
    return parts.join(' · ');
  };
  /** «Изменить» у вкладки — свой инструмент с теми же входами и возвратом на этот итог (§11 «Изменить»). */
  const change = (section: string) => {
    const photo = value(keys.photos[0]);
    const text = value(keys.text);
    const common = { ...(photo ? { product_photo: photo } : {}), ...(platform ? { platform } : {}) };
    const target =
      section === 'photos'
        ? { screen: 'pshoot', options: { ...common, ...(value(keys.photoCount) ? { count: value(keys.photoCount) } : {}) } }
        : section === 'slides'
          ? { screen: 'infog', options: { ...common, ...(text ? { text } : {}) } }
          : section === 'video'
            ? { screen: 'video', preset: 'cover', options: common }
            : { screen: 'text', tool: 'seo', options: { ...common, ...(text ? { text } : {}) } };
    openStudioTarget({ ...target, ret: true });
  };
  const warnings =
    kind === 'infog' && done
      ? stopWords(
          loaded.shape.slides.map(slide => `${slide.title}\n${slide.body}\n${slide.text}`),
          platform,
        )
      : null;
  const ratio = kind === 'post' ? (value(keys.format) === '1:1' ? 1 : 4 / 5) : platform === 'fresh' ? 1 : 3 / 4;
  /* ── результат на месте героя ── */
  let visual: ReactNode = null;
  if (done && kind === 'mcard' && activeTab)
    visual = (
      <div className="px-res">
        <div className="seg px-tabs" role="tablist" aria-label={uiText('Работы')}>
          {sections.map(section => (
            <button
              key={section.kind}
              type="button"
              role="tab"
              className={section.kind === activeTab.kind ? 'on' : ''}
              aria-selected={section.kind === activeTab.kind}
              onClick={() => setTab(section.kind)}
            >
              {uiText(TAB[section.kind])}
            </button>
          ))}
        </div>
        <div className="px-tab" role="tabpanel" aria-label={uiText(TAB[activeTab.kind])}>
          <div className="px-cap">
            <span>{caption(activeTab.kind, activeTab.indices.length)}</span>
            <button type="button" className="lnk" disabled={off} onClick={() => change(activeTab.kind)}>
              {uiText('Изменить')}
            </button>
          </div>
          {(activeTab.kind === 'photos' || activeTab.kind === 'slides') && (
            <div className="px-grid">
              {assetsOf(activeTab.indices)
                .filter(item => item.mime.startsWith('image/'))
                .map((item, index) => (
                  <a key={item.index} href={item.url} download={item.name}>
                    <img src={item.url} alt={`${uiText(TAB[activeTab.kind])} ${index + 1}`} />
                  </a>
                ))}
            </div>
          )}
          {activeTab.kind === 'video' &&
            assetsOf(activeTab.indices)
              .filter(item => item.mime.startsWith('video/'))
              .map(item => <video key={item.index} src={item.url} controls playsInline />)}
          {activeTab.kind === 'description' &&
            assetsOf(activeTab.indices)
              .filter(item => item.text !== undefined)
              .map(item => <TextBlock key={item.index} title="Описание товара" value={item.text ?? ''} />)}
        </div>
      </div>
    );
  else if (done && kind === 'mcard' && loaded.items.length)
    visual = (
      <div className="px-grid">
        {loaded.items.map(item => (
          <a key={item.index} href={item.url} download={item.name}>
            {item.mime.startsWith('image/') ? <img src={item.url} alt={item.name} /> : item.name}
          </a>
        ))}
      </div>
    );
  else if (done && images.length)
    visual = (
      <SlideCarousel
        kind={kind}
        items={images}
        slides={loaded.shape.slides}
        locked={off || edit.blocked}
        edit={{ save: edit.save, busy: edit.busy, pending: !!edit.pending }}
      />
    );
  const settings =
    kind === 'infog'
      ? [
          value(keys.style) ? uiFormat('Оформление «{0}»', label(INFOG_STYLES, value(keys.style))) : '',
          platformName,
          images.length ? count(images.length, SLIDE_FORMS) : '',
        ]
      : kind === 'post'
        ? [
            value(keys.style) ? uiFormat('Стиль «{0}»', label(POST_STYLES, value(keys.style))) : '',
            images.length ? count(images.length, SLIDE_FORMS) : '',
            value(keys.format) ? label(POST_FORMATS, value(keys.format)).toLowerCase() : '',
            value(keys.tone) ? uiFormat('тон {0}', label(POST_TONES, value(keys.tone)).toLowerCase()) : '',
          ]
        : [];
  const subject = (value(keys.text) ?? '').split('\n')[0];
  const card =
    done && settings.some(Boolean) ? (
      <div className="ar-q px-card">
        <span>{settings.filter(Boolean).join(' · ')}</span>
        {subject && <b>{subject}</b>}
      </div>
    ) : undefined;
  const extra = (
    <>
      {loaded.loading && <p role="status">{uiText('Загружаем результат…')}</p>}
      {loaded.error && (
        <p role="alert">
          {uiText('Не удалось загрузить результат.')}{' '}
          <button type="button" onClick={loaded.retry}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {edit.pending && !edit.busy && (
        <p role="status">
          {uiText(edit.pending.child ? 'Перерисовываем слайд…' : 'Правка ещё не подтверждена.')}{' '}
          {!edit.pending.child && (
            <button type="button" onClick={() => void edit.resume()}>
              {uiText('Продолжить проверку')}
            </button>
          )}
        </p>
      )}
      {edit.error && <p role="alert">{uiText(edit.error)}</p>}
      {warnings && warnings.words.length > 0 && (
        <p className="note warn">
          {uiFormat(
            'Уберите {0}: {1} не принимает на фото {2}',
            andList(warnings.words, 3, true),
            platformName || uiText('площадка'),
            andList(warnings.groups, 3),
          )}
        </p>
      )}
      {kind === 'post' && loaded.shape.caption && <TextBlock title="Текст поста" value={loaded.shape.caption} limit={1024} />}
      {kind === 'infog' &&
        loaded.items
          .filter(item => item.text !== undefined)
          .map(item => <TextBlock key={item.index} title="Текст" value={item.text ?? ''} />)}
      {message && <p role="alert">{uiText(message)}</p>}
      {!!shown?.result?.available && shown.result.assets.length > 0 && (
        <div className="px-files" role="group" aria-label={uiText('Файлы результата')}>
          <span className="gl">{uiFormat('Файлы результата · {0}', shown.result.assets.length)}</span>
          {shown.result.assets.map(asset => (
            <button key={asset.index} type="button" disabled={off} onClick={() => void state.openResult(asset.index)}>
              {asset.file_name}
            </button>
          ))}
        </div>
      )}
      {state.result && (
        <figure className="px-file">
          {state.result.mime.startsWith('image/') && <img src={state.result.url} alt={uiText('Результат')} />}
          {state.result.mime.startsWith('video/') && <video src={state.result.url} controls playsInline />}
          {state.result.mime.startsWith('text/') && <pre>{state.result.text}</pre>}
          <a href={state.result.url} download={state.result.name}>
            {uiText('Скачать')}
          </a>
        </figure>
      )}
    </>
  );
  const tabAssets = activeTab ? assetsOf(activeTab.indices) : [];
  const acts: TailAction[] = [
    kind === 'mcard'
      ? {
          label: 'Скачать',
          icon: 'download',
          disabled: loaded.loading || !tabAssets.length,
          onClick: () =>
            download(
              activeTab && activeTab.indices.length > 1 ? activeTab.downloadUrl : '',
              activeTab ? `steos-card-${activeTab.kind}.zip` : 'steos-card.zip',
              tabAssets[0],
            ),
        }
      : {
          label: kind === 'post' ? 'Скачать' : 'Скачать всё',
          icon: 'download',
          disabled: loaded.loading || !loaded.items.length,
          onClick: () =>
            download(
              loaded.items.length > 1 ? loaded.shape.archiveUrl : '',
              kind === 'post' ? 'steos-post.zip' : 'steos-infographic.zip',
              loaded.items[0],
            ),
        },
    { label: 'Ещё вариант', icon: 'refresh', onClick: leave },
  ];
  const photo = value(keys.photos[0]);
  const next: TailAction[] =
    kind === 'infog'
      ? [
          { label: 'Изменить', icon: 'edit', onClick: leave },
          {
            label: 'Описание товара',
            onClick: () =>
              openStudioTarget({
                screen: 'text',
                tool: 'seo',
                options: { ...(subject ? { text: value(keys.text) } : {}), ...(platform ? { platform } : {}) },
              }),
          },
          {
            label: 'Видео товара, 5 сек',
            onClick: () => openStudioTarget({ screen: 'video', preset: 'cover', options: photo ? { product_photo: photo } : {} }),
          },
        ]
      : kind === 'post'
        ? [
            { label: 'Изменить стиль', icon: 'edit', onClick: leave },
            { label: 'Сделать рилс', onClick: () => openStudioTarget({ screen: 'reels' }) },
          ]
        : [];
  const doneWords = sections.map(section => DONE_WORD[section.kind]).filter(Boolean);
  return (
    <OrderTail
      state={state}
      kind={kind}
      className={`product-order product-order-${kind}`}
      texts={{
        ...texts,
        doneP:
          kind === 'mcard' && doneWords.length
            ? `${uiFormat('Готово всё: {0}.', andList(doneWords, 4))} ${uiText(texts.doneP)}`
            : texts.doneP,
      }}
      hero={{
        shape: 'frame',
        ratio,
        picture: hero ? <img src={hero} alt="" /> : undefined,
        result: visual ?? undefined,
      }}
      card={card}
      result={extra}
      acts={acts}
      next={next}
      modAction={texts.reject}
      onRetry={onRetry}
    />
  );
}
